// src/pages/ArtworkCreatePage.js
import React, { useState } from 'react';
import { http } from "../lib/axios";
import { useNavigate } from 'react-router-dom';
import { TextField, Button, MenuItem, Typography, Box, Stack, FormControl, InputLabel, Select } from '@mui/material';
import subcategorySchema from '../utils/subcategorySchema';

const artworkTypes = Object.keys(subcategorySchema);
const RESPONSIBILITY_OPTIONS = ["HOST", "ARTIST"];

export default function ArtworkCreatePage({
  onCreated,
  inline = false,
  hostManaged = false,      // ← NEW: gallery-host mode
  managedArtistId = null    // ← NEW: which artist this host is creating for
}) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    artwork_type: 'other',
    subcategories: {},
    dimensions: '',
    representation_mode: hostManaged ? 'gallery_represented' : 'artflow',
  });
  const [images, setImages] = useState([]);
  const [error, setError] = useState(null);
  const [representationTerms, setRepresentationTerms] = useState({
    commission_artist: '70',
    commission_host: '30',
    insurance_responsibility: 'HOST',
    shipping_to_host_responsibility: 'HOST',
    shipping_from_host_responsibility: 'HOST',
    start_date: '',
    end_date: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubcategoryChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      subcategories: {
        ...prev.subcategories,
        [name]: value
      }
    }));
  };

  const handleFileChange = (e) => {
    setImages(Array.from(e.target.files));
  };

  const handleTermsChange = (field) => (e) => {
    setRepresentationTerms((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCreateArtwork = async () => {
    // Safety: if we're in host-managed mode, we *must* have an artist id
    if (hostManaged && !managedArtistId) {
      setError('No artist selected for this gallery-managed artwork.');
      return;
    }

    try {
      const data = new FormData();

      // Core artwork fields
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'subcategories') {
          data.append(key, JSON.stringify(value));
        } else {
          data.append(key, value);
        }
      });

      // Host-managed: tell backend which artist this is for
      if (hostManaged && managedArtistId) {
        data.append('artist_id', managedArtistId);
        data.append('representation_terms', JSON.stringify({
          ...representationTerms,
          commission_artist: Number(representationTerms.commission_artist),
          commission_host: Number(representationTerms.commission_host),
          price: Number(formData.price),
        }));
      }

      // Image uploads match the ArtworkSerializer / managed artwork serializer contract.
      images.forEach(img => data.append('uploaded_images', img));

      // Choose endpoint based on mode
      const url = hostManaged
        ? '/hosts/managed-artworks/'
        : '/artworks/';

      const res = await http.post(url, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const created = res?.data;

      if (typeof onCreated === 'function') {
        try { onCreated(created); } catch (_) {}
      }

      if (!inline) {
        // Normal page mode: go back to artist dashboard
        navigate('/dashboard/artist');
      } else {
        // Inline (modal/embedded) mode: reset form
        setFormData({
          title: '',
          description: '',
          price: '',
          artwork_type: 'other',
          subcategories: {},
          dimensions: '',
          representation_mode: hostManaged ? 'gallery_represented' : 'artflow',
        });
        setRepresentationTerms({
          commission_artist: '70',
          commission_host: '30',
          insurance_responsibility: 'HOST',
          shipping_to_host_responsibility: 'HOST',
          shipping_from_host_responsibility: 'HOST',
          start_date: '',
          end_date: '',
        });
        setImages([]);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to upload artwork.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    await handleCreateArtwork();
  };

  const selectedSubcategories = subcategorySchema[formData.artwork_type] || {};

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {hostManaged ? 'Add New Artwork (Gallery Managed)' : 'Upload New Artwork'}
      </Typography>
      {hostManaged && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This will create an accepted gallery representation agreement immediately. No platform fee is currently applied to gallery-represented sales.
        </Typography>
      )}
      {error && <Typography color="error">{error}</Typography>}
      <form onSubmit={handleSubmit}>
        <TextField
          name="title"
          label="Title"
          fullWidth
          margin="normal"
          value={formData.title}
          onChange={handleChange}
          sx={{ backgroundColor: 'white' }}
        />
        <TextField
          name="description"
          label="Description"
          fullWidth
          multiline
          rows={4}
          margin="normal"
          value={formData.description}
          onChange={handleChange}
          sx={{ backgroundColor: 'white' }}
        />
        <TextField
          name="price"
          label="Price"
          fullWidth
          type="number"
          margin="normal"
          value={formData.price}
          onChange={handleChange}
          sx={{ backgroundColor: 'white' }}
        />
        <TextField
          select
          name="artwork_type"
          label="Artwork Type"
          fullWidth
          margin="normal"
          value={formData.artwork_type}
          onChange={handleChange}
          sx={{ backgroundColor: 'white' }}
        >
          {artworkTypes.map(type => (
            <MenuItem key={type} value={type}>{type}</MenuItem>
          ))}
        </TextField>

        {/* Dynamically render subcategory fields */}
        {Object.entries(selectedSubcategories).map(([subcatName, options]) => (
          <TextField
            key={subcatName}
            select
            name={subcatName}
            label={subcatName}
            fullWidth
            margin="normal"
            value={formData.subcategories[subcatName] || ''}
            onChange={handleSubcategoryChange}
            sx={{ backgroundColor: 'white' }}
          >
            {options.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
        ))}

        <TextField
          name="dimensions"
          label="Dimensions (e.g. 20x30 inches or 50x70 cm)"
          fullWidth
          margin="normal"
          value={formData.dimensions}
          onChange={handleChange}
          sx={{ backgroundColor: 'white' }}
        />

        {!hostManaged && (
          <>
            <TextField
              select
              name="representation_mode"
              label="Representation Mode"
              fullWidth
              margin="normal"
              value={formData.representation_mode}
              onChange={handleChange}
              sx={{ backgroundColor: 'white' }}
            >
              <MenuItem value="artflow">ARTflow</MenuItem>
              <MenuItem value="gallery_represented">Gallery Represented</MenuItem>
            </TextField>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Choose <strong>Gallery Represented</strong> only if this artwork is intended to be sold through a gallery representation relationship. Doing so keeps the artwork out of normal ARTflow hosting requests and direct buyer purchase until a gallery host formally claims it through an accepted representation agreement.
            </Typography>
          </>
        )}

        <Button
          variant="outlined"
          component="label"
          sx={{ my: 2, color: 'primary', backgroundColor: 'white' }}
        >
          Upload Images
          <input type="file" accept="image/*" hidden multiple onChange={handleFileChange} />
        </Button>
        <Typography variant="body2">
          {images.length} image(s) selected
        </Typography>

        {hostManaged && (
          <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.72)' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Gallery Representation Terms</Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Artist Commission (%)"
                  type="number"
                  value={representationTerms.commission_artist}
                  onChange={handleTermsChange('commission_artist')}
                  fullWidth
                />
                <TextField
                  label="Host Commission (%)"
                  type="number"
                  value={representationTerms.commission_host}
                  onChange={handleTermsChange('commission_host')}
                  fullWidth
                />
              </Stack>
              <FormControl fullWidth>
                <InputLabel id="gallery-insurance-label">Insurance Responsibility</InputLabel>
                <Select
                  labelId="gallery-insurance-label"
                  label="Insurance Responsibility"
                  value={representationTerms.insurance_responsibility}
                  onChange={handleTermsChange('insurance_responsibility')}
                >
                  {RESPONSIBILITY_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="gallery-shipping-to-label">Shipping to Host</InputLabel>
                <Select
                  labelId="gallery-shipping-to-label"
                  label="Shipping to Host"
                  value={representationTerms.shipping_to_host_responsibility}
                  onChange={handleTermsChange('shipping_to_host_responsibility')}
                >
                  {RESPONSIBILITY_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="gallery-shipping-from-label">Shipping from Host</InputLabel>
                <Select
                  labelId="gallery-shipping-from-label"
                  label="Shipping from Host"
                  value={representationTerms.shipping_from_host_responsibility}
                  onChange={handleTermsChange('shipping_from_host_responsibility')}
                >
                  {RESPONSIBILITY_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={representationTerms.start_date}
                  onChange={handleTermsChange('start_date')}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={representationTerms.end_date}
                  onChange={handleTermsChange('end_date')}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Stack>
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 2 }}
        >
          Submit
        </Button>
      </form>
    </Box>
  );
}
